import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { ArrowRight } from 'lucide-react';
import { formatPercentage } from '@/lib/utils';
import { useFormatting } from '@/hooks/useFormatting';
import { usePermissions } from '@/hooks/usePermissions';
import { useDataSourceContext } from '@/contexts/DataSourceContext';
import { DataSourceConfig } from '@/types/dataSource';
import { DashboardMetrics, getRevenueForecast, getExpenseForecast } from '@/services/metricsService';
import { financialData } from '@/data/financial-systems';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

interface VarianceCardProps {
  metrics: DashboardMetrics;
  onNavigate?: (page: string) => void;
}

type Period = 'week' | 'month';

// Build daily cumulative forecast vs actual data points
function buildChartData(
  period: Period,
  dataSources: DataSourceConfig[]
) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const days = period === 'week' ? 7 : 30;
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  const completedTxns = financialData.bankTransactions.filter(
    (t: { date: Date; amount: number; status: string }) =>
      t.status === 'Settled' && t.date >= startDate && t.date <= now
  );

  const points: Array<{ label: string; Forecasted: number; Actual: number }> = [];

  for (let i = 0; i <= days; i++) {
    const day = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    const dayEnd = new Date(day.getTime() + 24 * 60 * 60 * 1000 - 1);

    // Cumulative forecast: sum forecast from startDate to this day
    const forecastedNet =
      getRevenueForecast(dataSources, startDate, dayEnd) -
      getExpenseForecast(dataSources, startDate, dayEnd);

    // Cumulative actual: sum completed txns from startDate to this day
    const actualNet = completedTxns
      .filter(t => t.date <= dayEnd)
      .reduce((sum, t) => sum + t.amount, 0);

    const label =
      period === 'week'
        ? day.toLocaleDateString('en-GB', { weekday: 'short' })
        : i % 5 === 0
          ? day.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          : '';

    points.push({
      label,
      Forecasted: Math.round(forecastedNet),
      Actual: Math.round(actualNet),
    });
  }

  return points;
}

// Custom tooltip
function ChartTooltip({
  active,
  payload,
  label,
  formatCurrency,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  formatCurrency: (v: number, p: 'full') => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-background shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-neutral-800 mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }} className="font-medium">
          {p.name}: {formatCurrency(p.value, 'full')}
        </p>
      ))}
    </div>
  );
}

export default function VarianceCard({ metrics, onNavigate }: VarianceCardProps) {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();
  const { dataSources } = useDataSourceContext();
  const [period, setPeriod] = useState<Period>('week');

  if (!hasPermission('view_variance_card')) {
    return null;
  }

  const forecast = period === 'week' ? metrics.weeklyForecast : metrics.monthlyForecast;
  const actual = period === 'week' ? metrics.weeklyActual : metrics.monthlyActual;
  const variancePercent = forecast !== 0 ? ((actual - forecast) / Math.abs(forecast)) * 100 : 0;
  const isNegative = variancePercent < 0;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const chartData = useMemo(
    () => buildChartData(period, dataSources),
    [period, dataSources]
  );

  const forecastColour = '#6b7280'; // mid grey
  const actualColour = '#111827'; // black

  return (
    <Card data-tour="variance">
      <CardHeader className="flex flex-row items-center justify-between border-b">
        <div className="flex items-center gap-1.5">
          <span className="text-lg font-semibold text-neutral-900">Variance</span>
          <InfoTooltip
            title={`Variance ${period === 'week' ? 'This Week' : 'This Month'}`}
            content={`Compares your forecasted cash vs actual cash ${period === 'week' ? 'this week' : 'this month'}. Helps track forecast accuracy and identify patterns.`}
          />
        </div>
          {/* Week / Month pill toggle */}
          <div className="flex items-center rounded-full border border-border bg-muted/50 p-0.5 text-xs">
            <button
              onClick={() => setPeriod('week')}
              className={`rounded-full px-3 py-1 transition-colors ${
                period === 'week'
                  ? 'bg-background text-foreground font-semibold shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setPeriod('month')}
              className={`rounded-full px-3 py-1 transition-colors ${
                period === 'month'
                  ? 'bg-background text-foreground font-semibold shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Month
            </button>
          </div>
      </CardHeader>
      <CardContent className="p-5 flex flex-col gap-3">
        {/* KPI row — sits above the chart */}
        <div className="flex items-center gap-8">
          {/* Big % variance */}
          <div>
            <div
              className="text-2xl font-bold tracking-tight"
              style={{ color: isNegative ? 'var(--mc-error-icon)' : 'var(--mc-success-icon)' }}
            >
              {formatPercentage(variancePercent)}
            </div>
            <p className="text-xs font-normal text-neutral-800 mt-0.5">
              variance {period === 'week' ? 'this week' : 'this month'}
            </p>
          </div>

          <div className="w-px h-8 bg-border" />

          {/* Forecasted */}
          <div>
            <p className="text-2xl font-bold tracking-tight" style={{ color: forecastColour }}>{formatCurrency(forecast, 'full')}</p>
            <p className="text-xs font-normal text-neutral-800 mt-0.5">Forecasted</p>
          </div>

          {/* Actual */}
          <div>
            <p className="text-2xl font-bold tracking-tight" style={{ color: actualColour }}>{formatCurrency(actual, 'full')}</p>
            <p className="text-xs font-normal text-neutral-800 mt-0.5">Actual</p>
          </div>
        </div>

        {/* Line chart — full width below KPIs */}
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis
                width={50}
                tick={{ fontSize: 12 }}
                tickFormatter={(v: number) => `£${(v / 1000).toFixed(0)}K`}
              />
              <RechartsTooltip
                content={<ChartTooltip formatCurrency={formatCurrency} />}
              />
              <Line
                type="monotone"
                dataKey="Forecasted"
                stroke={forecastColour}
                strokeWidth={2}
                dot={false}
                strokeDasharray="4 3"
              />
              <Line
                type="monotone"
                dataKey="Actual"
                stroke={actualColour}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Bottom — View Reasons */}
        {hasPermission('view_variance_analysis_screen') && (
          <div className="border-t border-border pt-3 flex justify-end">
            <button
              onClick={() => onNavigate?.('variance')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                text-neutral-900
                hover:bg-teal-50 hover:text-teal-700"
            >
              View Reasons
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
